import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Printer, Plus, AlertTriangle, Download, Loader2, Calendar, History, RotateCcw, Eye, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import api from "../lib/api";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";

export default function InvoiceOrigin() {
  const { id: batchId } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  
  const [batch, setBatch] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [error, setError] = useState(null);
  const [showModifyDialog, setShowModifyDialog] = useState(false);
  const [empresaActiva, setEmpresaActiva] = useState(null);
  
  // Estados para el diálogo de selección de fecha
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [dateOption, setDateOption] = useState("lote"); // "lote" o "manual"
  const [manualDate, setManualDate] = useState("");
  
  // Estados para restaurar factura OLD
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedOldInvoice, setSelectedOldInvoice] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener datos del lote y configuración
        const [batchRes, configRes] = await Promise.all([
          api.get(`/batches/${batchId}`),
          api.get("/config")
        ]);
        
        setBatch(batchRes.data);
        
        // Obtener empresa activa
        const empresa = configRes.data.empresas?.find(e => e.id === configRes.data.empresa_activa_id);
        setEmpresaActiva(empresa);
        
        // Obtener proveedor
        if (batchRes.data.proveedor_id) {
          try {
            const supplierRes = await api.get(`/suppliers/${batchRes.data.proveedor_id}`);
            setSupplier(supplierRes.data);
          } catch (e) {
            console.log("No se pudo cargar proveedor");
          }
        }
        
        // Obtener facturas existentes
        try {
          const invoicesRes = await api.get(`/invoices/batch/${batchId}`);
          const originInvoices = invoicesRes.data.filter(inv => inv.tipo === "origen");
          const activeInvoice = originInvoices.find(inv => inv.estado === "activo");
          const oldInvoices = originInvoices.filter(inv => inv.estado === "old");
          
          setInvoice(activeInvoice || null);
          setInvoiceHistory(oldInvoices);
        } catch (e) {
          console.log("No hay facturas");
        }
        
        setLoading(false);
      } catch (err) {
        setError("Error al cargar los datos del lote");
        setLoading(false);
      }
    };
    
    fetchData();
  }, [batchId]);

  const handlePrint = () => {
    window.print();
  };

  // Limpiar nombre (sin comas ni puntos)
  const cleanName = (name) => {
    return (name || "").replace(/[,\.]/g, "").trim();
  };

  // Generar nombre del archivo según fórmula:
  // Factura Origen: AAAAMMDD "PROVEEDOR" NÚMEROFACTURAORIGEN CONTENEDOR/AWB
  // Usa la fecha de CREACIÓN del lote (created_at), no la fecha actual
  const generateFileName = () => {
    const fechaLote = batch?.created_at ? new Date(batch.created_at) : new Date();
    const dateStr = fechaLote.toISOString().slice(0,10).replace(/-/g, '');
    const proveedorName = cleanName(supplier?.nombre || batch?.proveedor_nombre || "PROVEEDOR");
    const facturaNum = batch?.factura_origen || invoice?.numero_factura || "";
    const envio = batch?.contenedor || batch?.awb_aereo || "";
    return `${dateStr} ${proveedorName} ${facturaNum} ${envio}`.trim();
  };

  // Guardar como PDF con nombre específico
  const handleSavePdf = async () => {
    setSavingPdf(true);
    try {
      const filename = generateFileName();
      const element = document.getElementById("invoice-container");
      
      if (!element) {
        toast.error("Error: no se encontró el contenido de la factura");
        return;
      }

      const opt = {
        margin: [5, 5, 5, 5], // top, left, bottom, right en mm
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, width: 794 }, // 210mm a 96dpi aprox
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: 'avoid-all' }
      };

      // Generar PDF como blob y base64 simultáneamente
      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');
      
      // Descargar localmente
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Guardar en el servidor automáticamente (en segundo plano)
      try {
        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);
        reader.onloadend = async () => {
          try {
            const base64data = reader.result.split(',')[1]; // Quitar prefijo data:application/pdf;base64,
            const saveResponse = await api.post(`/batches/${batchId}/save-invoice-pdf`, {
              pdf_base64: base64data,
              filename: filename,
              tipo: "origen"
            });
            console.log("[SAVE_PDF] PDF guardado en servidor:", saveResponse.data);
          } catch (saveErr) {
            console.error("[SAVE_PDF] Error al guardar PDF en servidor:", saveErr);
            toast.warning("PDF descargado pero no se pudo guardar en el servidor");
          }
        };
      } catch (saveError) {
        console.warn("No se pudo guardar el PDF en el servidor:", saveError);
        toast.warning("PDF descargado pero no se pudo guardar en el servidor");
      }

      toast.success("PDF guardado correctamente");
    } catch (error) {
      console.error("Error generando PDF:", error);
      toast.error("Error al guardar el PDF");
    } finally {
      setSavingPdf(false);
    }
  };

  // Verificar si se debe mostrar el diálogo de fecha
  const checkDateAndCreate = () => {
    if (!batch?.created_at) {
      // Si no hay fecha de creación del lote, crear directamente
      handleCreateInvoice(new Date().toISOString().slice(0, 10));
      return;
    }
    
    const loteDate = new Date(batch.created_at).toISOString().slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    
    if (loteDate !== today) {
      // Fechas diferentes, mostrar diálogo
      setManualDate(today);
      setDateOption("lote");
      setShowDateDialog(true);
    } else {
      // Misma fecha, crear directamente con fecha del lote
      handleCreateInvoice(loteDate);
    }
  };

  const handleCreateInvoice = async (fechaFactura) => {
    setShowDateDialog(false);
    setCreating(true);
    try {
      const response = await api.post("/invoices", {
        batch_id: batchId,
        tipo: "origen",
        fecha_factura: fechaFactura
      });
      setInvoice(response.data);
      toast.success("Factura creada correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear la factura");
    }
    setCreating(false);
  };

  const handleDateDialogConfirm = () => {
    const fechaSeleccionada = dateOption === "lote" 
      ? new Date(batch.created_at).toISOString().slice(0, 10)
      : manualDate;
    handleCreateInvoice(fechaSeleccionada);
  };

  const handleModifyInvoice = async () => {
    setShowModifyDialog(false);
    setCreating(true);
    try {
      const response = await api.put(`/invoices/${invoice.id}/modify`);
      setInvoice(response.data.new_invoice);
      setInvoiceHistory(prev => [...prev, { ...invoice, estado: "old", nombre_archivo: response.data.old_invoice }]);
      toast.success(`Factura modificada. Versión anterior guardada como ${response.data.old_invoice}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al modificar la factura");
    }
    setCreating(false);
  };

  // Formatear número con separador de miles
  const formatNumber = (num, decimals = 2) => {
    if (!num && num !== 0) return "0,00";
    return num.toLocaleString("es-ES", { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error || "Lote no encontrado"}
        </div>
        <Button onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver
        </Button>
      </div>
    );
  }

  // Si no hay factura creada, mostrar botón para crear
  if (!invoice) {
    const emisor = supplier || {
      nombre: batch.proveedor_nombre || "PROVEEDOR NO ASIGNADO",
      identificacion_fiscal: "N/A",
      direccion: "N/A"
    };

    return (
      <div className="min-h-screen bg-gray-100">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver
            </Button>
            <h1 className="text-xl font-bold">Factura de Origen - {batch.numero_lote}</h1>
          </div>
        </div>
        
        <div className="p-6">
          <div className="bg-white max-w-2xl mx-auto p-8 rounded-lg shadow-lg text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Crear Factura de Proveedor</h2>
            <p className="text-gray-600 mb-6">
              No existe una factura de origen para este lote. 
              Al crear la factura, se generará un documento con los datos actuales del lote.
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
              <h3 className="font-semibold text-gray-700 mb-2">Datos del lote:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li><strong>Lote:</strong> {batch.numero_lote}</li>
                <li><strong>Proveedor:</strong> {emisor.nombre}</li>
                <li><strong>Producto:</strong> {batch.producto_nombre}</li>
                <li><strong>Peso Neto:</strong> {formatNumber(batch.peso_neto)} kg</li>
                <li><strong>Precio Coste:</strong> ${formatNumber(batch.precio_coste)}/kg</li>
              </ul>
            </div>
            
            <Button 
              size="lg" 
              onClick={checkDateAndCreate}
              disabled={creating}
              className="bg-blue-900 hover:bg-blue-800"
            >
              {creating ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Crear Factura de Proveedor
                </span>
              )}
            </Button>
          </div>
        </div>
        
        {/* Diálogo de selección de fecha */}
        <Dialog open={showDateDialog} onOpenChange={setShowDateDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Seleccionar fecha de la factura
              </DialogTitle>
              <DialogDescription>
                La fecha actual es diferente a la fecha de creación del lote. 
                ¿Qué fecha desea usar para la factura?
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <RadioGroup value={dateOption} onValueChange={setDateOption} className="space-y-3">
                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50">
                  <RadioGroupItem value="lote" id="fecha-lote" />
                  <Label htmlFor="fecha-lote" className="flex-1 cursor-pointer">
                    <span className="font-medium">Fecha de creación del lote</span>
                    <span className="block text-sm text-slate-500">
                      {batch?.created_at ? (() => {
                        const datePart = batch.created_at.split('T')[0];
                        const parts = datePart.split('-');
                        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : '-';
                      })() : '-'}
                    </span>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50">
                  <RadioGroupItem value="manual" id="fecha-manual" />
                  <Label htmlFor="fecha-manual" className="flex-1 cursor-pointer">
                    <span className="font-medium">Fecha manual</span>
                  </Label>
                </div>
              </RadioGroup>
              
              {dateOption === "manual" && (
                <div className="mt-3 pl-7">
                  <Input
                    type="date"
                    value={manualDate}
                    onChange={(e) => setManualDate(e.target.value)}
                    className="w-48"
                  />
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDateDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleDateDialogConfirm}
                disabled={dateOption === "manual" && !manualDate}
                className="bg-blue-900 hover:bg-blue-800"
              >
                Crear Factura
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Mostrar factura existente
  const emisor = supplier || {
    nombre: invoice.emisor_nombre,
    identificacion_fiscal: invoice.emisor_rif,
    direccion: invoice.emisor_direccion
  };
  
  // Calcular empaques únicos usados en las tallas
  const getEmpaquesUsados = () => {
    if (batch?.tallas && batch.tallas.length > 0) {
      const empaques = batch.tallas
        .filter(t => t.talla && (t.num_bultos > 0 || t.peso_por_bulto > 0))
        .map(t => t.empaque_seleccionado)
        .filter(e => e && e.trim() !== '');
      
      // Quitar duplicados y ordenar
      const uniqueEmpaques = [...new Set(empaques)];
      
      if (uniqueEmpaques.length > 0) {
        return uniqueEmpaques.join(" / ");
      }
    }
    // Fallback al empaque del batch o invoice
    return batch?.empaque || invoice?.empaque || "N/A";
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Barra de herramientas (no se imprime) */}
      <div className="print:hidden bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>
          <h1 className="text-xl font-bold">Factura de Origen - {batch.numero_lote}</h1>
          {invoice.version > 1 && (
            <span className="text-sm text-gray-500">(Versión {invoice.version})</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowModifyDialog(true)}>
            Modificar Factura
          </Button>
          <Button variant="outline" onClick={handleSavePdf} disabled={savingPdf}>
            {savingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Guardar PDF
          </Button>
          <Button onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" /> Imprimir
          </Button>
        </div>
      </div>

      {/* Historial de versiones - Interactivo */}
      {invoiceHistory.length > 0 && (
        <div className="print:hidden bg-amber-50 border-b border-amber-200 px-6 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <History className="w-4 h-4 text-amber-700" />
            <span className="text-sm font-medium text-amber-800">Historial:</span>
            {invoiceHistory.map((h, idx) => (
              <DropdownMenu key={h.id || idx}>
                <DropdownMenuTrigger asChild>
                  <button className="text-sm text-amber-700 hover:text-amber-900 hover:underline cursor-pointer font-medium">
                    {h.nombre_archivo}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onClick={() => {
                      // Generar PDF y abrir en nueva pestaña
                      toast.info("Generando vista previa...");
                      // Por ahora navegar a una vista específica
                      window.open(`/invoice-old/${h.id}`, '_blank');
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver factura
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedOldInvoice(h);
                      setShowRestoreDialog(true);
                    }}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restaurar como actual
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
          </div>
        </div>
      )}

      {/* Contenido de la factura - formato A4 */}
      <div className="p-6 print:p-0">
        <div 
          ref={printRef}
          id="invoice-container"
          className="bg-white mx-auto shadow-lg print:shadow-none"
          style={{ 
            width: "200mm", 
            maxWidth: "200mm",
            padding: "10mm",
            boxSizing: "border-box"
          }}
        >
          {/* HEADER */}
          <div className="border-b-2 border-gray-300 pb-4 mb-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <img 
                  src="/lg_global_logo.png" 
                  alt="LG Global Seafood" 
                  className="h-14 mb-2"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <h2 className="text-base font-bold text-gray-800">{emisor.nombre}</h2>
                <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                  <p><span className="font-semibold">RIF:</span> {emisor.identificacion_fiscal}</p>
                  <p><span className="font-semibold">Dirección:</span> {emisor.direccion}</p>
                  <p><span className="font-semibold">VUCE:</span> CRE/N°31006020250811alg0</p>
                </div>
              </div>
              <div className="text-right">
                <div className="bg-blue-900 text-white px-4 py-1.5 rounded">
                  <h1 className="text-xl font-bold">FACTURA</h1>
                </div>
              </div>
            </div>
          </div>

          {/* DATOS DE FACTURA Y CLIENTE */}
          <div className="grid grid-cols-2 gap-6 mb-4 text-xs">
            <div className="space-y-1">
              <div className="grid grid-cols-2 gap-1">
                <span className="font-semibold text-gray-600">N.º de factura:</span>
                <span className="font-bold">{invoice.numero_factura}</span>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <span className="font-semibold text-gray-600">Fecha de vencimiento:</span>
                <span>{invoice.fecha_vencimiento}</span>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <span className="font-semibold text-gray-600">NÚMERO DE CONTROL:</span>
                <span className="font-bold">{invoice.numero_control}</span>
              </div>
              <div className="grid grid-cols-2 gap-1 pt-1 border-t mt-1">
                <span className="font-semibold text-gray-600">Fecha:</span>
                <span>{invoice.fecha_factura}</span>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <span className="font-semibold text-gray-600">Id. del cliente:</span>
                <span>2501</span>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <span className="font-semibold text-gray-600">Condiciones:</span>
                <span>TRANSFERENCIA 30 DÍAS</span>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <h3 className="font-bold text-blue-900 mb-2 border-b pb-1 text-sm">FACTURAR A (consignatario)</h3>
              <div className="space-y-0.5">
                <p className="font-semibold">{empresaActiva?.nombre || "EMPRESA"}</p>
                <p><span className="font-semibold">CIF:</span> {empresaActiva?.cif || ""}</p>
                <p><span className="font-semibold">Dirección:</span> {empresaActiva?.direccion || ""}</p>
                <p><span className="font-semibold">EMAIL:</span> {empresaActiva?.email || ""}</p>
              </div>
            </div>
          </div>

          {/* TABLA DE PRODUCTOS */}
          <div className="mb-4">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-blue-900 text-white">
                  <th className="p-2 text-left font-semibold">Cantidad</th>
                  <th className="p-2 text-left font-semibold">Descripción</th>
                  <th className="p-2 text-right font-semibold">Precio por unidad</th>
                  <th className="p-2 text-right font-semibold">Total de línea</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-2">{formatNumber(invoice.cantidad, 2)}</td>
                  <td className="p-2">
                    {invoice.producto_nombre} {batch?.bl_maritimo && batch?.contenedor 
                      ? `${batch.bl_maritimo} ${batch.contenedor}` 
                      : batch?.bl_maritimo || batch?.awb_aereo || ""}
                  </td>
                  <td className="p-2 text-right">${formatNumber(invoice.precio_unitario, 2)}</td>
                  <td className="p-2 text-right font-semibold">${formatNumber(invoice.subtotal_usd, 2)}</td>
                </tr>
                <tr className="border-b h-6"><td colSpan="4"></td></tr>
                <tr className="border-b h-6"><td colSpan="4"></td></tr>
              </tbody>
            </table>

            <div className="mt-2 text-xs text-gray-600 flex justify-between">
              <p><span className="font-semibold">Alérgenos:</span> {batch.alergenos || invoice.alergenos || "N/A"}</p>
              <p><span className="font-semibold">Kilos de plásticos no reutilizables:</span> {formatNumber(invoice.kilos_plasticos, 2)}</p>
            </div>
          </div>

          {/* TOTALES Y LOTE */}
          <div className="grid grid-cols-2 gap-6 mb-4">
            <div className="space-y-1 text-xs">
              <div className="flex justify-between border-b pb-1">
                <span className="font-semibold">Subtotal</span>
                <span>${formatNumber(invoice.subtotal_usd, 2)}</span>
              </div>
              <div className="flex justify-between border-b pb-1">
                <span className="font-semibold text-blue-900">TOTAL USD {batch?.tipo_envio === "aereo" ? "FOB" : "CFR"}</span>
                <span className="font-bold">${formatNumber(invoice.total_usd, 2)}</span>
              </div>
              <div className="flex justify-between border-b pb-1">
                <span className="font-semibold">TOTAL BS {batch?.tipo_envio === "aereo" ? "FOB" : "CFR"}</span>
                <span>Bs.S{formatNumber(invoice.total_bs, 2)}</span>
              </div>
              <div className="flex justify-between border-b pb-1 bg-yellow-50 -mx-1 px-1">
                <span className="font-semibold text-blue-900">TOTAL EUR {batch?.tipo_envio === "aereo" ? "FOB" : "CFR"}</span>
                <span className="font-bold">{formatNumber(invoice.total_eur, 2)} €</span>
              </div>
              <div className="flex justify-between text-gray-600 pt-1 text-[10px]">
                <div>
                  <p>Cambio BCV (USD→BS): {formatNumber(invoice.cambio_bcv, 4)}</p>
                  <p>Cotiz. EUR/USD: {formatNumber(invoice.cambio_eur, 4)}</p>
                </div>
                <span>{invoice.fecha_factura}</span>
              </div>
            </div>

            <div className="border-4 border-blue-900 rounded-lg p-3 flex items-center justify-center">
              <div className="text-center">
                <p className="text-xs font-semibold text-gray-600">LOTE</p>
                <p className="text-2xl font-bold text-blue-900">{invoice.lote_numero}</p>
              </div>
            </div>
          </div>

          {/* INFORMACIÓN DEL PRODUCTO */}
          <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
            <div className="bg-gray-50 p-3 rounded space-y-0.5">
              <p><span className="font-semibold">CAPTURA SILVESTRE:</span></p>
              <p><span className="font-semibold">PRODUCTO PESCA EXTRACTIVA CAPTURADO:</span> Nasas (FPO).</p>
              <p><span className="font-semibold">PAÍS DE ORIGEN:</span> VENEZUELA</p>
              <p><span className="font-semibold">PRODUCIDO POR:</span> INVERSIONES FRISUR, C.A. RIF.: J-41253799-7</p>
              <p><span className="font-semibold">SPES:</span> SPES-6479</p>
              <p><span className="font-semibold">EXPORTADO POR:</span> {emisor.nombre} RIF {emisor.identificacion_fiscal}</p>
            </div>

            <div className="bg-blue-50 p-3 rounded border-2 border-blue-200">
              <div className="text-center space-y-1">
                <p className="font-bold text-blue-900 text-base">MANTENER CONGELADO A (-18ºC)</p>
                <p className="font-bold text-lg text-gray-800">{invoice.producto_nombre?.split(" ")[0] || batch.producto_nombre?.split(" ")[0] || "PRODUCTO"}</p>
                <p className="text-sm"><span className="font-semibold">PRESENTACIÓN:</span> {batch.presentacion || invoice.presentacion || "N/A"}</p>
                <p className="text-sm"><span className="font-semibold">EMPAQUE:</span> {getEmpaquesUsados()}</p>
              </div>
            </div>
          </div>

          {/* FOOTER - texto pequeño y centrado - posición más abajo */}
          <div className="border-t pt-2 mt-auto" id="invoice-footer" style={{ marginTop: 'auto' }}>
            <div style={{ height: '30px' }}></div>
            <p className="font-bold text-gray-800 mb-2 text-center" style={{ fontSize: "10px" }}>Nº CONTROL: 00-{invoice.numero_control}</p>
            <div className="text-center space-y-0.5" style={{ fontSize: "7px", color: "#4b5563", lineHeight: '1.3' }}>
              <p className="font-semibold italic" style={{ fontSize: "8px" }}>ESTA FORMA LIBRE VA SIN TACHADURA NI ENMIENDAS</p>
              <p style={{ fontSize: "6px" }}>ARTES GRAFICAS DIYHEAN 2007, C.A. Av. Lecuna, Urb. El Conde, Edif Catuche, Sótano 2, Local 2D2-16, Caracas - Telfs: 0212-5741420 / 0414-3565806 RIF: J-29396546-2</p>
              <p style={{ fontSize: "6px" }}>Nº Providencia: SENIAT/01/00277 de Fecha: 19-02-2008 - REGIÓN CAPITAL / FORMA LIBRE / Nº de Control desde el Nº 00-000001 hasta el Nº 00-000100 de Fecha: 07/08/2025</p>
              <p className="font-bold text-gray-800 pt-2" style={{ fontSize: "9px" }}>ORIGINAL - CLIENTE</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog de confirmación para modificar */}
      <AlertDialog open={showModifyDialog} onOpenChange={setShowModifyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              ¿Modificar esta factura?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción creará una nueva versión de la factura con los datos actualizados del lote.
              <br /><br />
              La factura actual se archivará como <strong>{invoice?.numero_factura}_OLD{String(invoiceHistory.length + 1).padStart(3, "0")}</strong> y no se eliminará.
              <br /><br />
              Los valores monetarios (cambio BCV, EUR) se mantendrán de la factura original.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleModifyInvoice}>
              Sí, modificar factura
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de confirmación para restaurar factura OLD */}
      <AlertDialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-600" />
              Restaurar Factura Antigua
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea restaurar la factura <strong>{selectedOldInvoice?.nombre_archivo}</strong>?
              <br /><br />
              La factura actual será archivada como OLD y esta versión antigua pasará a ser la activa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!selectedOldInvoice) return;
                setRestoring(true);
                try {
                  await api.post(`/invoices/old/${selectedOldInvoice.id}/restore`);
                  toast.success("Factura restaurada correctamente");
                  // Recargar la página para ver los cambios
                  window.location.reload();
                } catch (err) {
                  toast.error("Error al restaurar la factura");
                  console.error(err);
                } finally {
                  setRestoring(false);
                  setShowRestoreDialog(false);
                }
              }}
              disabled={restoring}
            >
              {restoring ? "Restaurando..." : "Restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Estilos de impresión */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          
          /* Ocultar ABSOLUTAMENTE TODO excepto la factura */
          body > *:not(#root),
          #root > *:not([class*="min-h-screen"]),
          nav,
          aside,
          header,
          button,
          .sticky,
          .print\\:hidden,
          [class*="sidebar"],
          [class*="fixed"],
          [class*="sticky"] {
            display: none !important;
            visibility: hidden !important;
            width: 0 !important;
            height: 0 !important;
            overflow: hidden !important;
            position: absolute !important;
            left: -9999px !important;
          }
          
          html, body, #root {
            width: 210mm !important;
            height: 297mm !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: hidden !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* El contenedor de la factura */
          #invoice-container {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            max-height: 297mm !important;
            margin: 0 !important;
            padding: 8mm !important;
            background: white !important;
            box-shadow: none !important;
            overflow: hidden !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            font-size: 11px !important;
          }
          
          #invoice-container * {
            visibility: visible !important;
          }
          
          /* Colores de impresión */
          .bg-blue-900 {
            background-color: #1e3a8a !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .text-white {
            color: white !important;
          }
          
          .bg-gray-50, .bg-blue-50 {
            background-color: #f9fafb !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .border-blue-200 {
            border-color: #bfdbfe !important;
          }
        }
      `}</style>
    </div>
  );
}
